import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { Data } from '@lfb/core';
/**
 * Error contract for every API call (docs/FRONTEND.md):
 * - `status`: HTTP status, or 0 when the network request itself failed.
 * - `code`: the server's `error.code` (e.g. `VALIDATION_ERROR`, `FORBIDDEN`, `CONFLICT`), or a
 *   client code (`NETWORK_ERROR`, `INVALID_RESPONSE`).
 * - `requestId`: `error.requestId` from the body, falling back to the `X-Request-Id` header.
 *   Shown to users so support can correlate server logs.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code = 'UNKNOWN_ERROR',
    public requestId?: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}
export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;
/** Optimistic-concurrency failure from a stale `version` on update. */
export const isVersionConflict = (error: unknown) =>
  error instanceof ApiError &&
  error.status === 409 &&
  (error.code === 'VERSION_CONFLICT' || /record changed/i.test(error.message));

/* Session expiry: any authenticated call returning 401 ends the browser session once. */
const expiryListeners = new Set<() => void>();
const storeListeners = new Set<() => void>();
let sessionExpired = false;
const subscribeStore = (listener: () => void) => {
  storeListeners.add(listener);
  return () => {
    storeListeners.delete(listener);
  };
};
function markSessionExpired() {
  if (sessionExpired) return;
  sessionExpired = true;
  expiryListeners.forEach((listener) => listener());
  storeListeners.forEach((listener) => listener());
}
/** Called once when an authenticated request returns 401. Returns an unsubscribe function. */
export function onSessionExpired(listener: () => void) {
  expiryListeners.add(listener);
  return () => {
    expiryListeners.delete(listener);
  };
}
/** Reset after a successful sign-in so the next expiry is reported again. */
export function clearSessionExpired() {
  if (!sessionExpired) return;
  sessionExpired = false;
  storeListeners.forEach((listener) => listener());
}
export const useSessionExpired = () =>
  useSyncExternalStore(
    subscribeStore,
    () => sessionExpired,
    () => false,
  );
/** Sign-in, session bootstrap and public calls report 401 to their caller instead. */
const ownsAuthentication = (path: string) =>
  path.startsWith('/auth/') ||
  path === '/session' ||
  path.startsWith('/session?') ||
  path === '/config' ||
  path.startsWith('/invitations');

export async function api<T>(
  path: string,
  options: {
    method?: string;
    data?: unknown;
    workspaceId?: string;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch('/api/v1' + path, {
      method: options.method || 'GET',
      credentials: 'include',
      signal: options.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(options.workspaceId ? { 'X-Workspace-Id': options.workspaceId } : {}),
        ...options.headers,
      },
      ...(options.data !== undefined ? { body: JSON.stringify(options.data) } : {}),
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(
      typeof navigator !== 'undefined' && navigator.onLine === false
        ? 'You are offline. Check your connection and try again.'
        : 'Unable to reach LFB. Check your connection and try again.',
      0,
      'NETWORK_ERROR',
    );
  }
  const headerRequestId = response.headers.get('X-Request-Id') || undefined;
  const raw = await response.text();
  let body: {
    data: T;
    error?: { message?: string; code?: string; requestId?: string; details?: unknown };
  };
  try {
    body = JSON.parse(raw) as typeof body;
  } catch {
    throw new ApiError(
      'The service is temporarily unavailable. Please try again.',
      response.status || 503,
      'INVALID_RESPONSE',
      headerRequestId,
    );
  }
  if (!response.ok) {
    const error = new ApiError(
      body?.error?.message || 'Unable to complete this request',
      response.status,
      body?.error?.code || 'HTTP_' + response.status,
      body?.error?.requestId || headerRequestId,
      body?.error?.details,
    );
    if (
      response.status === 401 &&
      !ownsAuthentication(path) &&
      ['UNAUTHENTICATED', 'SESSION_EXPIRED', 'HTTP_401'].includes(error.code)
    )
      markSessionExpired();
    throw error;
  }
  return body.data;
}
/** Client errors are deterministic; retry only network and server failures, once. */
export const shouldRetry = (failureCount: number, error: unknown) =>
  failureCount < 1 && !(error instanceof ApiError && error.status >= 400 && error.status < 500);
export const useApi = <T>(
  path: string,
  workspaceId?: string,
  enabled = true,
  refreshInterval?: number,
) =>
  useQuery<T>({
    queryKey: [path, workspaceId],
    queryFn: ({ signal }) => api<T>(path, { workspaceId, signal }),
    enabled,
    staleTime: 10000,
    retry: shouldRetry,
    refetchInterval: refreshInterval,
    refetchIntervalInBackground: false,
  });

export interface RecordPage<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}
export function nextRecordPage<T>(last: RecordPage<T>): number | undefined {
  return last.items.length > 0 && last.page * last.limit < last.total ? last.page + 1 : undefined;
}

/** Pages remain cached per workspace/filter; canceled queries cannot append into a new list. */
export function useInfiniteApi<T extends { id: string }>(
  path: string,
  workspaceId?: string,
  limit = 40,
) {
  const key = JSON.stringify([path, workspaceId, limit]);
  const [allKey, setAllKey] = useState('');
  const query = useInfiniteQuery({
    queryKey: ['infinite-records', path, workspaceId, limit],
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      api<RecordPage<T>>(
        `${path}${path.includes('?') ? '&' : '?'}limit=${limit}&page=${pageParam}`,
        { workspaceId, signal },
      ),
    getNextPageParam: nextRecordPage<T>,
    enabled: !!workspaceId,
    staleTime: 10000,
    retry: shouldRetry,
  });
  const items = useMemo(() => {
    const seen = new Set<string>();
    return (query.data?.pages.flatMap((page) => page.items) || []).filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [query.data]);
  const { fetchNextPage, hasNextPage, isFetching, isFetchNextPageError } = query;
  const loadingAll = allKey === key && !!hasNextPage && !isFetchNextPageError;
  useEffect(() => {
    if (loadingAll && !isFetching) void fetchNextPage();
  }, [loadingAll, isFetching, fetchNextPage]);
  const loadMore = useCallback(() => {
    setAllKey('');
    if (hasNextPage && !isFetching) void fetchNextPage();
  }, [hasNextPage, isFetching, fetchNextPage]);
  const loadAll = useCallback(() => {
    setAllKey(key);
    if (hasNextPage && !isFetching) void fetchNextPage();
  }, [key, hasNextPage, isFetching, fetchNextPage]);
  return {
    ...query,
    items,
    total: query.data?.pages.at(-1)?.total || 0,
    loadingAll,
    loadMore,
    loadAll,
    resetKey: key,
  };
}
export function downloadCsv(filename: string, columns: string[], rows: Data[]) {
  const quote = (v: unknown) => {
    let s = String(v ?? '');
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    return '"' + s.replaceAll('"', '""') + '"';
  };
  const blob = new Blob(
    [
      '\uFEFF' +
        [
          columns.map(quote).join(','),
          ...rows.map((r) => columns.map((c) => quote(r[c])).join(',')),
        ].join('\r\n'),
    ],
    { type: 'text/csv;charset=utf-8' },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadExcel(filename: string, columns: string[], rows: Data[]) {
  const { writeWorkbook } = await import('@lfb/core');
  const bytes = await writeWorkbook(columns, rows);
  const url = URL.createObjectURL(
    new Blob([bytes as Uint8Array<ArrayBuffer>], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
